import * as Yup from 'yup';

import {
  DISCOUNT_TYPE,
  VOUCHER_APPLY_TYPE,
  VOUCHER_USE_TYPE,
  isBookeseDiscountUseType,
  isBookesePublicUseType,
} from 'constants/voucherCampaign';
import {
  INVALID_CODE_MESSAGE,
  VOUCHER_CODE_MAX_LENGTH,
  VOUCHER_CODE_MIN_LENGTH,
  VOUCHER_CODE_REGEX,
} from 'components/Modals/ModalCreateVoucher/voucherCodeValidate';

const optionalPositiveNumber = Yup.number()
  .transform((value, originalValue) => (
    originalValue === '' || originalValue === null ? undefined : value
  ))
  .nullable()
  .notRequired()
  .typeError('Giá trị phải là số')
  .moreThan(0, 'Giá trị phải lớn hơn 0');

const campaignValidationSchema = Yup.object({
  campaignName: Yup.string()
    .trim()
    .required('Tên chiến dịch là bắt buộc')
    .min(3, 'Tên chiến dịch phải có tối thiểu 3 kí tự')
    .max(50, 'Tên chiến dịch chỉ được tối đa 50 kí tự'),

  voucherType: Yup.string()
    .required('Loại voucher là bắt buộc'),

  discountType: Yup.string().when('voucherType', {
    is: isBookeseDiscountUseType,
    then: Yup.string().required('Loại giảm giá là bắt buộc'),
    otherwise: Yup.string().notRequired(),
  }),

  discountValue: Yup.number()
    .transform((value, originalValue) => (
      originalValue === '' || originalValue === null ? undefined : value
    ))
    .when(['voucherType', 'discountType'], {
      is: (voucherType, discountType) => (
        isBookeseDiscountUseType(voucherType)
        && discountType === DISCOUNT_TYPE.PERCENT
      ),
      then: Yup.number()
        .typeError('Giá trị giảm phải là số')
        .required('Giá trị giảm là bắt buộc')
        .moreThan(0, 'Giá trị giảm phải lớn hơn 0')
        .max(100, 'Giá trị giảm tối đa là 100%'),
      otherwise: Yup.number().when('voucherType', {
        is: isBookeseDiscountUseType,
        then: Yup.number()
          .typeError('Giá trị giảm phải là số')
          .required('Giá trị giảm là bắt buộc')
          .moreThan(0, 'Giá trị giảm phải lớn hơn 0'),
        otherwise: Yup.number().notRequired(),
      }),
    }),

  maxDiscountAmount: Yup.number()
    .transform((value, originalValue) => (
      originalValue === '' || originalValue === null ? undefined : value
    ))
    .when(['voucherType', 'discountType'], {
      is: (voucherType, discountType) => (
        voucherType === VOUCHER_USE_TYPE.BOOKESE
        && discountType === DISCOUNT_TYPE.PERCENT
      ),
      then: Yup.number()
        .typeError('Giảm tối đa phải là số')
        .required('Giảm tối đa là bắt buộc')
        .moreThan(0, 'Giảm tối đa phải lớn hơn 0'),
      otherwise: Yup.number().when(['voucherType', 'discountType'], {
        is: (voucherType, discountType) => (
          isBookesePublicUseType(voucherType)
          && discountType === DISCOUNT_TYPE.PERCENT
        ),
        then: optionalPositiveNumber,
        otherwise: Yup.number().notRequired(),
      }),
    }),

  code: Yup.string().when('voucherType', {
    is: isBookesePublicUseType,
    then: Yup.string()
      .trim()
      .required('Mã voucher là bắt buộc')
      .min(VOUCHER_CODE_MIN_LENGTH, INVALID_CODE_MESSAGE)
      .max(VOUCHER_CODE_MAX_LENGTH, INVALID_CODE_MESSAGE)
      .matches(VOUCHER_CODE_REGEX, INVALID_CODE_MESSAGE),
    otherwise: Yup.string().notRequired(),
  }),

  totalVoucher: Yup.number()
    .transform((value, originalValue) => (
      originalValue === '' || originalValue === null ? undefined : value
    ))
    .when('voucherType', {
      is: isBookesePublicUseType,
      then: Yup.number()
        .typeError('Tổng số voucher phải là số')
        .required('Tổng số voucher là bắt buộc')
        .integer('Tổng số voucher phải là số nguyên')
        .moreThan(0, 'Tổng số voucher phải lớn hơn 0'),
      otherwise: Yup.number().notRequired(),
    }),

  maxUsesPerUser: Yup.number()
    .transform((value, originalValue) => (
      originalValue === '' || originalValue === null ? undefined : value
    ))
    .when('voucherType', {
      is: isBookesePublicUseType,
      then: Yup.number()
        .nullable()
        .notRequired()
        .typeError('Số lần dùng tối đa phải là số')
        .integer('Số lần dùng tối đa phải là số nguyên')
        .moreThan(0, 'Số lần dùng tối đa phải lớn hơn 0'),
      otherwise: Yup.number().notRequired(),
    }),

  applyType: Yup.string().when('voucherType', {
    is: isBookesePublicUseType,
    then: Yup.string().required('Điều kiện áp dụng là bắt buộc'),
    otherwise: Yup.string().notRequired(),
  }),

  minBookingAmount: Yup.number()
    .transform((value, originalValue) => (
      originalValue === '' || originalValue === null ? undefined : value
    ))
    .when(['voucherType', 'applyType'], {
      is: (voucherType, applyType) => (
        isBookesePublicUseType(voucherType)
        && applyType === VOUCHER_APPLY_TYPE.MIN_BOOKING_AMOUNT
      ),
      then: Yup.number()
        .typeError('Đơn hàng tối thiểu phải là số')
        .required('Đơn hàng tối thiểu là bắt buộc')
        .moreThan(0, 'Đơn hàng tối thiểu phải lớn hơn 0'),
      otherwise: Yup.number().notRequired(),
    }),

  startAt: Yup.date()
    .nullable()
    .required('Ngày bắt đầu là bắt buộc'),

  endAt: Yup.date()
    .nullable()
    .when('voucherType', {
      is: isBookesePublicUseType,
      then: Yup.date().nullable().notRequired(),
      otherwise: Yup.date()
        .nullable()
        .required('Ngày kết thúc là bắt buộc'),
    }),
});

export default campaignValidationSchema;
